/**
 * AppointmentChecklistDialog.js
 *
 * A dismissible dialog showing Start and End appointment checklists for TOC
 * Adult Autism clinicians. Items are a numbered list only — not an interactive
 * checklist — so clinicians are not led to believe ticks are saved or required.
 *
 * Architecture:
 * - Controlled Radix Dialog (not AlertDialog — this is informational, not
 *   a blocking clinical confirmation).
 * - Custom underline tabs matching HistorySourceDataDialog pattern.
 *
 * Props:
 *   open            {boolean}   Whether the dialog is visible
 *   onOpenChange    {function}  Radix open state callback
 *   activeTab       {'start'|'end'}  Which tab to show when the dialog opens
 *   onTabChange     {function}  Called with 'start'|'end' when the user switches tabs
 *   appointmentType {'developmentalHistory'|'observation'}  Determines End checklist item 1
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/outline';
import {
  START_APPOINTMENT_ITEMS,
  getEndAppointmentItems,
} from './appointmentChecklistContent';

const TABS = [
  { id: 'start', label: 'Start of appointment' },
  { id: 'end', label: 'End of appointment' },
];

export default function AppointmentChecklistDialog({
  open,
  onOpenChange,
  activeTab = 'start',
  onTabChange,
  appointmentType = 'developmentalHistory',
}) {
  const [currentTab, setCurrentTab] = useState(activeTab);

  // Sync internal tab state when controlled activeTab changes (e.g. button
  // pressed while dialog was already open on a different tab).
  useEffect(() => {
    setCurrentTab(activeTab);
  }, [activeTab]);

  const handleTabSwitch = useCallback((tabId) => {
    setCurrentTab(tabId);
    onTabChange?.(tabId);
  }, [onTabChange]);

  const items = currentTab === 'start'
    ? START_APPOINTMENT_ITEMS
    : getEndAppointmentItems(appointmentType);

  // Smooth height change when switching Start/End tabs. Same pattern as
  // ManageNotificationsDialog / ManageOrgConfigDialog: lock the wrapper to
  // the previous pixel height, then measure the new content and CSS-transition
  // to it. CSS cannot animate from `auto`, so we re-measure after the new
  // list has painted.
  const contentRef = useRef(null);
  const [animatedHeight, setAnimatedHeight] = useState('auto');

  const measureAndAnimate = useCallback(() => {
    if (!contentRef.current) return;
    setAnimatedHeight(`${contentRef.current.scrollHeight}px`);
  }, []);

  useEffect(() => {
    if (!open) {
      setAnimatedHeight('auto');
      return undefined;
    }
    const timer = setTimeout(measureAndAnimate, 30);
    return () => clearTimeout(timer);
  }, [open, currentTab, appointmentType, measureAndAnimate]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className="radix-dialog-content-inset-motion fixed inset-0 z-50 flex items-center justify-center p-4 outline-none"
          onClick={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}
        >
          <div className="w-[95vw] max-w-[580px] max-h-[85vh] overflow-y-auto rounded-lg bg-white shadow-lg">
            <div
              className="overflow-hidden transition-[height] duration-300 ease-in-out"
              style={{ height: animatedHeight }}
              data-testid="appointment-checklist-height"
            >
              <div ref={contentRef} className="p-5 md:p-6">
                {/* Header: title + close */}
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title className="text-base font-semibold text-gray-900">
                    Appointment Checklists
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                      aria-label="Close"
                    >
                      <XIcon className="h-5 w-5 text-gray-500" />
                    </button>
                  </Dialog.Close>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 mb-4" role="tablist">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={currentTab === tab.id}
                      className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        currentTab === tab.id
                          ? 'border-primary text-primary'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => handleTabSwitch(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <Dialog.Description className="sr-only">
                  {currentTab === 'start'
                    ? 'Items to cover at the start of the appointment'
                    : 'Items to cover at the end of the appointment'}
                </Dialog.Description>

                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 leading-snug">
                  {items.map((item, index) => (
                    <li key={`${currentTab}-${index}`}>
                      {item}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
